import { useDuplicateProductMutation } from '@codezo/graphql';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

type Target = { id: string; name: string };

/**
 * "Add to my products" for a community product: a confirm, then a copy into the organization's
 * own products. The backend copies it as an independent product (images included), so later
 * changes to the community product never touch it. Lands on the new product.
 *
 * Owns its confirm dialog so every caller gets the same one, and closes it before navigating —
 * the product page stays mounted across /products/:id changes, so a dialog left open would
 * reappear over the copy.
 *
 * Render `addDialog` once; call `requestAdd(product)` from a button or menu item.
 */
export function useAddToMyProducts() {
	const navigate = useNavigate();
	const [target, setTarget] = useState<Target | null>(null);
	const [duplicateProduct, { loading }] = useDuplicateProductMutation();

	const confirmAdd = async () => {
		if (!target) return;
		try {
			const { data } = await duplicateProduct({
				variables: { input: { productId: target.id } },
			});
			const newId = data?.duplicateProduct?.referenceKey;
			if (!data?.duplicateProduct?.success || !newId) {
				toast.error(data?.duplicateProduct?.message ?? 'Failed to add product');
				return;
			}
			toast.success(`${target.name} added to your products`);
			setTarget(null);
			navigate(`/products/${newId}`);
		} catch {
			toast.error('Failed to add product');
		}
	};

	const addDialog = (
		<ConfirmDialog
			open={!!target}
			onOpenChange={(open) => !open && setTarget(null)}
			title="Add to my products"
			description={`A copy of "${target?.name ?? ''}" will be added to your products, where you can edit it freely. Later changes to the community product will not affect your copy.`}
			confirmLabel="Add product"
			loadingLabel="Adding…"
			variant="default"
			loading={loading}
			onConfirm={confirmAdd}
		/>
	);

	return { requestAdd: setTarget, addDialog };
}
